/* ============================================================
   Toast.jsx — the js/ui.js toast, as a provider + portal.

   Timing is preserved exactly: 4200ms visible, then `.out` is added and the
   node is removed 300ms later. The `.out` exit transition lives in the frozen
   CSS, so collapsing this into a single removal makes toasts vanish instead of
   sliding away.
   ============================================================ */

import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import Icon from './icons'
import { setRequestAlertHandler } from '../lib/apiClient'

const VISIBLE_MS = 4200
// A toast with an action (e.g. "Reopen form") has to stay long enough to click.
const ACTION_VISIBLE_MS = 12000
const EXIT_MS = 300

const CONFIG = {
  success: { icon: 'check-circle', tone: 'i-green', title: 'Success' },
  error: { icon: 'x-circle', tone: 'i-red', title: 'Error' },
  info: { icon: 'info', tone: 'i-blue', title: 'Notice' },
  warning: { icon: 'alert', tone: 'i-amber', title: 'Warning' },
}

const ToastContext = createContext(null)

// Module-level escape hatch, so non-component code (the ~40 call sites that were
// `App.toast(...)`) can raise a toast without threading the hook through.
let externalToast = () => {}
export function toast(msg, type, title, options) {
  externalToast(msg, type, title, options)
}

export function useToast() {
  const ctx = useContext(ToastContext)
  if (!ctx) throw new Error('useToast must be used inside <ToastProvider>')
  return ctx
}

export default function ToastProvider({ children }) {
  const [items, setItems] = useState([])
  const timers = useRef(new Map())

  const remove = useCallback((id) => {
    setItems((cur) => cur.map((t) => (t.id === id ? { ...t, leaving: true } : t)))
    const t = setTimeout(() => {
      setItems((cur) => cur.filter((x) => x.id !== id))
      timers.current.delete(id)
    }, EXIT_MS)
    timers.current.set(`exit-${id}`, t)
  }, [])

  const push = useCallback(
    (msg, type = 'info', title, { action } = {}) => {
      const id = `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`
      setItems((cur) => [...cur, { id, msg, type, title, action, leaving: false }])
      timers.current.set(id, setTimeout(() => remove(id), action ? ACTION_VISIBLE_MS : VISIBLE_MS))
      return id
    },
    [remove],
  )

  useEffect(() => {
    externalToast = push
    setRequestAlertHandler(push)
    return () => {
      externalToast = () => {}
      setRequestAlertHandler(() => {})
    }
  }, [push])

  useEffect(() => {
    const map = timers.current
    return () => map.forEach(clearTimeout)
  }, [])

  const value = useMemo(() => ({ toast: push, dismiss: remove }), [push, remove])

  return (
    <ToastContext.Provider value={value}>
      {children}
      {createPortal(
        <div className="toast-root" role="status" aria-live="polite">
          {items.map((t) => {
            const cfg = CONFIG[t.type] || CONFIG.info
            return (
              <div key={t.id} className={`toast${t.leaving ? ' out' : ''}`}>
                <span className={`toast-icn ${cfg.tone}`}>
                  <Icon name={cfg.icon} />
                </span>
                <div className="toast-body">
                  <div className="toast-title">{t.title || cfg.title}</div>
                  <div className="toast-msg">{t.msg}</div>
                  {t.action && (
                    <button
                      className="toast-action"
                      onClick={() => {
                        t.action.onClick()
                        remove(t.id)
                      }}
                    >
                      {t.action.label}
                    </button>
                  )}
                </div>
                <button className="toast-close" onClick={() => remove(t.id)} aria-label="Dismiss">
                  <Icon name="x" />
                </button>
              </div>
            )
          })}
        </div>,
        document.body,
      )}
    </ToastContext.Provider>
  )
}
